import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    DocumentSchemaRecord,
    createDocumentSchema,
    listDocumentSchemas,
    updateDocumentSchema,
} from '../../../services/documentSchemaService';
import { SchemaEditor } from './SchemaEditor';
import { SchemaDocsPanel } from './SchemaDocsPanel';
import { SchemaVersionDiff } from './SchemaVersionDiff';
import { EditorContext } from './types';
import {
    ChevronRight,
    ChevronDown,
    Plus,
    Copy,
    GitBranch,
    ArrowLeftRight,
} from 'lucide-react';

interface SchemaGroup {
    key: string;
    ownerType: string;
    versions: DocumentSchemaRecord[];
    latest: DocumentSchemaRecord;
}

const ManageDocumentSchemasPage: React.FC = () => {
    const [schemas, setSchemas] = useState<DocumentSchemaRecord[]>([]);
    const [editorContext, setEditorContext] = useState<EditorContext | null>(
        null
    );
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
    const [diffKey, setDiffKey] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState<string>('');

    const groups = useMemo<SchemaGroup[]>(() => {
        const map = new Map<string, DocumentSchemaRecord[]>();
        for (const schema of schemas) {
            const existing = map.get(schema.key) || [];
            existing.push(schema);
            map.set(schema.key, existing);
        }

        return Array.from(map.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, versions]) => {
                const sorted = [...versions].sort(
                    (a, b) => b.version - a.version
                );
                return {
                    key,
                    ownerType: sorted[0].ownerType,
                    versions: sorted,
                    latest: sorted[0],
                };
            });
    }, [schemas]);

    const diffVersions = useMemo(() => {
        if (!diffKey) return null;
        const group = groups.find((g) => g.key === diffKey);
        return group && group.versions.length >= 2 ? group.versions : null;
    }, [diffKey, groups]);

    useEffect(() => {
        const fetchSchemas = async () => {
            try {
                setIsLoading(true);
                const result = await listDocumentSchemas();
                setSchemas(result);
                if (result.length > 0) {
                    const sorted = [...result].sort(
                        (a, b) => b.version - a.version
                    );
                    setEditorContext({
                        mode: 'edit',
                        existingRecord: sorted[0],
                    });
                }
            } catch (e) {
                setError('Failed to load document schemas');
            } finally {
                setIsLoading(false);
            }
        };
        fetchSchemas();
    }, []);

    const handleSave = useCallback(
        async (nextSchema: Partial<DocumentSchemaRecord>) => {
            if (!editorContext?.existingRecord) return;
            setIsSaving(true);
            setStatusMessage('');
            setError(null);
            try {
                const updated = await updateDocumentSchema(
                    editorContext.existingRecord._id,
                    nextSchema
                );
                setSchemas((prev) =>
                    prev.map((s) => (s._id === updated._id ? updated : s))
                );
                setEditorContext({ mode: 'edit', existingRecord: updated });
                setStatusMessage(
                    `Saved ${updated.key} (v${updated.version}) at ${new Date().toLocaleTimeString()}`
                );
            } catch (e) {
                setError('Failed to save schema');
            } finally {
                setIsSaving(false);
            }
        },
        [editorContext]
    );

    const handleCreate = useCallback(
        async (
            record: Omit<DocumentSchemaRecord, '_id' | 'createdAt' | 'updatedAt'>
        ) => {
            setIsSaving(true);
            setStatusMessage('');
            setError(null);
            try {
                const created = await createDocumentSchema(record);
                setSchemas((prev) => [...prev, created]);
                setEditorContext({ mode: 'edit', existingRecord: created });
                setStatusMessage(
                    `Created ${created.key} (v${created.version}) at ${new Date().toLocaleTimeString()}`
                );
            } catch (e: any) {
                const message =
                    e?.response?.data?.message || 'Failed to create schema';
                setError(message);
            } finally {
                setIsSaving(false);
            }
        },
        []
    );

    const toggleExpanded = (key: string) => {
        setExpandedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const selectVersion = (record: DocumentSchemaRecord) => {
        setEditorContext({ mode: 'edit', existingRecord: record });
        setDiffKey(null);
    };

    const startNewSchema = () => {
        setEditorContext({ mode: 'create-new' });
        setDiffKey(null);
    };

    const startNewVersion = (baseRecord: DocumentSchemaRecord) => {
        setEditorContext({ mode: 'create-version', baseRecord });
        setDiffKey(null);
    };

    const startClone = (baseRecord: DocumentSchemaRecord) => {
        setEditorContext({ mode: 'clone', baseRecord });
        setDiffKey(null);
    };

    const startDiff = (key: string) => {
        setDiffKey(key);
        setEditorContext(null);
    };

    const selectedId =
        editorContext?.mode === 'edit'
            ? editorContext.existingRecord?._id
            : undefined;

    return (
        <div className="container mx-auto px-4 py-6 space-y-4">
            <div>
                <h1 className="text-2xl font-bold">Document Schemas</h1>
                <p className="text-sm text-muted-foreground">
                    Manage schema records: create, edit, version, and compare.
                </p>
            </div>

            {error && (
                <div className="border border-destructive/40 rounded p-3 text-destructive text-sm">
                    {error}
                </div>
            )}
            {statusMessage && (
                <div className="border border-green-500/40 rounded p-3 text-green-700 text-sm">
                    {statusMessage}
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-[340px_1fr] gap-4">
                <div className="space-y-4">
                    <div className="border rounded-lg overflow-hidden bg-card">
                        <div className="px-3 py-2 border-b flex items-center justify-between">
                            <span className="font-semibold text-sm">
                                Schemas
                            </span>
                            <button
                                type="button"
                                onClick={startNewSchema}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90"
                            >
                                <Plus className="w-3 h-3" />
                                New Schema
                            </button>
                        </div>
                        {isLoading ? (
                            <div className="p-3 text-sm text-muted-foreground">
                                Loading...
                            </div>
                        ) : groups.length === 0 ? (
                            <div className="p-3 text-sm text-muted-foreground">
                                No schemas found.
                            </div>
                        ) : (
                            <div className="max-h-[420px] overflow-auto">
                                {groups.map((group) => {
                                    const isExpanded = expandedKeys.has(
                                        group.key
                                    );
                                    return (
                                        <div key={group.key}>
                                            <div className="flex items-center border-b">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        toggleExpanded(
                                                            group.key
                                                        )
                                                    }
                                                    className="p-2 hover:bg-accent"
                                                >
                                                    {isExpanded ? (
                                                        <ChevronDown className="w-3.5 h-3.5" />
                                                    ) : (
                                                        <ChevronRight className="w-3.5 h-3.5" />
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        selectVersion(
                                                            group.latest
                                                        )
                                                    }
                                                    className={`flex-1 text-left px-1 py-2 text-sm hover:bg-accent ${
                                                        selectedId ===
                                                        group.latest._id
                                                            ? 'bg-accent'
                                                            : ''
                                                    }`}
                                                >
                                                    <div className="font-medium">
                                                        {group.key}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {group.ownerType} ·
                                                        latest v
                                                        {group.latest.version} ·{' '}
                                                        {group.latest.name}
                                                    </div>
                                                </button>
                                                <div className="flex items-center gap-0.5 pr-1">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            startNewVersion(
                                                                group.latest
                                                            )
                                                        }
                                                        className="p-1.5 rounded hover:bg-accent"
                                                        title="New Version"
                                                    >
                                                        <GitBranch className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            startClone(
                                                                group.latest
                                                            )
                                                        }
                                                        className="p-1.5 rounded hover:bg-accent"
                                                        title="Clone as New Schema"
                                                    >
                                                        <Copy className="w-3.5 h-3.5" />
                                                    </button>
                                                    {group.versions.length >=
                                                        2 && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                startDiff(
                                                                    group.key
                                                                )
                                                            }
                                                            className="p-1.5 rounded hover:bg-accent"
                                                            title="Compare Versions"
                                                        >
                                                            <ArrowLeftRight className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            {isExpanded && (
                                                <div className="bg-muted/30">
                                                    {group.versions.map(
                                                        (version) => (
                                                            <button
                                                                key={
                                                                    version._id
                                                                }
                                                                type="button"
                                                                onClick={() =>
                                                                    selectVersion(
                                                                        version
                                                                    )
                                                                }
                                                                className={`w-full text-left pl-9 pr-3 py-1.5 text-xs border-b hover:bg-accent ${
                                                                    selectedId ===
                                                                    version._id
                                                                        ? 'bg-accent font-medium'
                                                                        : ''
                                                                }`}
                                                            >
                                                                v
                                                                {
                                                                    version.version
                                                                }
                                                                <span className="text-muted-foreground ml-2">
                                                                    {new Date(
                                                                        version.updatedAt
                                                                    ).toLocaleDateString()}
                                                                </span>
                                                            </button>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <SchemaDocsPanel />
                </div>

                <div>
                    {diffKey && diffVersions ? (
                        <SchemaVersionDiff
                            versions={diffVersions}
                            onClose={() => setDiffKey(null)}
                        />
                    ) : editorContext ? (
                        <SchemaEditor
                            mode={editorContext.mode}
                            schemaRecord={editorContext.existingRecord}
                            baseRecord={editorContext.baseRecord}
                            isSaving={isSaving}
                            onSave={handleSave}
                            onCreate={handleCreate}
                        />
                    ) : (
                        <div className="border rounded-lg p-4 text-sm text-muted-foreground">
                            Select a schema to edit, or create a new one.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManageDocumentSchemasPage;
